import React, { createContext, useContext, useState, useEffect } from 'react';
import { loginUser } from '../api/auth';

interface User {
  token: string;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isInitializing: boolean;
  isLoggingIn: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setUser({ token });
    }
    setIsInitializing(false);
  }, []);

  const login = async (username: string, password: string) => {
    setIsLoggingIn(true);
    try {
      const data = await loginUser({ username, password });
      const newUser = { token: data.token };

      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refresh_token);
      localStorage.setItem('refreshTokenExpiration', data.refresh_token_expiration.toString());
      setUser(newUser);
    } catch (error) {
      throw error;
    } finally {
      setIsLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('refreshTokenExpiration');
    localStorage.removeItem('activeHouseholdId');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isInitializing, isLoggingIn }}>
      {children}
    </AuthContext.Provider>
  );
};